import React, { useEffect, useState } from 'react';
import axiosConn from '../../axiosConn';
import Share from '../../components/share/Share';
import RichShareButton from './RichShareButton';
import {
  SharedResultPresentation,
  reportShareAction,
} from './HitLabSharedResult';
import './hitLabResultShare.css';

export default function HitLabResultSharing({ result, authConfig }) {
  const [available, setAvailable] = useState(false),
    [open, setOpen] = useState(false);
  const [data, setData] = useState(null),
    [artwork, setArtwork] = useState(false);
  const [consent, setConsent] = useState(false),
    [busy, setBusy] = useState(false);
  const [error, setError] = useState(''),
    [image, setImage] = useState(''),
    [compose, setCompose] = useState(false);
  const version = result?.version;
  const endpoint = `hitlab/intake/results/${encodeURIComponent(
    version || '',
  )}/sharing`;
  useEffect(() => {
    let active = true;
    axiosConn
      .get('hitlab/result-sharing')
      .then((r) => {
        if (active) setAvailable(r.data.data.enabled);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);
  useEffect(() => {
    setOpen(false);
    setData(null);
    setConsent(false);
    setCompose(false);
  }, [version]);
  const load = async () => {
    const r = await axiosConn.get(`${endpoint}?artwork=${artwork}`, authConfig);
    setData(r.data.data);
    return r.data.data;
  };
  useEffect(() => {
    if (!open || !version || !authConfig) return undefined;
    let active = true;
    setError('');
    setData(null);
    setConsent(false);
    axiosConn
      .get(`${endpoint}?artwork=${artwork}`, authConfig)
      .then((r) => {
        if (active) setData(r.data.data);
      })
      .catch((e) => {
        if (active)
          setError(
            e.response?.data?.message || 'Preview could not load. Try again.',
          );
      });
    return () => {
      active = false;
    };
  }, [open, version, artwork, authConfig, endpoint]);
  useEffect(() => {
    if (!data || !open) return undefined;
    let active = true,
      url;
    setImage('');
    axiosConn
      .get(`${endpoint}/image?artwork=${artwork}`, {
        ...authConfig,
        responseType: 'blob',
      })
      .then((r) => {
        if (active) {
          url = URL.createObjectURL(r.data);
          setImage(url);
        }
      })
      .catch(() => {});
    return () => {
      active = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [data, open, endpoint, artwork, authConfig]);
  const change = async (enabled) => {
    setBusy(true);
    setError('');
    try {
      await axiosConn.post(
        endpoint,
        {
          enabled,
          revision: data.revision,
          previewHash: data.previewHash,
          includeArtwork: artwork,
          confirmPublish: consent,
        },
        authConfig,
      );
      setConsent(false);
      setCompose(false);
      await load();
    } catch (e) {
      setError(
        e.response?.data?.message ||
          'Sharing could not be updated. Reload the preview and try again.',
      );
    } finally {
      setBusy(false);
    }
  };
  if (!version || !available) return null;
  return (
    <section className="result-sharing" aria-label="Share your Listen Result">
      <p className="eyebrow">YOUR RESULT. YOUR CHOICE.</p>
      <h2>Keep it private. Or let people see it.</h2>
      <p>
        Your Listen Result is private until you enable its public page. Sharing
        is optional, whatever Joe’s decision.
      </p>
      <button className="primary" onClick={() => setOpen(!open)}>
        {open ? 'Close sharing preview' : 'Preview my share page'}
      </button>
      {error && <p role="alert">{error}</p>}
      {open &&
        (!data ? (
          <p role="status">Preparing your private preview…</p>
        ) : (
          <>
            {data.artworkAvailable && (
              <label className="sharing-consent">
                <input
                  type="checkbox"
                  checked={Boolean(
                    data.enabled ? data.result.coverArtUrl : artwork,
                  )}
                  disabled={busy || data.enabled}
                  onChange={(e) => setArtwork(e.target.checked)}
                />
                Include this artwork. I have permission to display it publicly.
              </label>
            )}
            <SharedResultPresentation
              data={data}
              preview
              imageUrl={image}
              compact
            />
            <div className="sharing-controls">
              <p>
                <strong>
                  {data.enabled
                    ? 'Your public link is enabled.'
                    : 'Your result is private.'}
                </strong>
              </p>
              {!data.enabled ? (
                <>
                  <label className="sharing-consent">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={(e) => setConsent(e.target.checked)}
                    />
                    Make this exact result version public, including the
                    displayed artist/song details, feedback, decision and dates.
                  </label>
                  <button
                    className="primary"
                    disabled={!consent || busy || !image}
                    onClick={() => change(true)}
                  >
                    Enable sharing for this result
                  </button>
                  {!image && (
                    <p className="share-note">
                      The image preview must load before publishing. Close and
                      reopen this preview to retry.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <RichShareButton
                    label="Share result link"
                    url={data.url}
                    title={`${result.songTitle} · JG HitLab`}
                    text={`Joe Galaxy’s Listen Result for ${result.artistName}`}
                    onAction={(a) => reportShareAction(data.id, a)}
                  />
                  <button
                    className="share-secondary"
                    onClick={() => setCompose(!compose)}
                  >
                    Create a JG Universe post
                  </button>
                  <button
                    className="share-secondary"
                    disabled={busy}
                    onClick={() => change(false)}
                  >
                    Disable public link
                  </button>
                  {compose && (
                    <div className="result-post-composer">
                      <p>
                        Review your post, then choose to publish. Enabling the
                        link does not publish a post.
                      </p>
                      <Share
                        initialDesc={`My JG HitLab Listen Result for ${result.songTitle}:\n${data.url}`}
                        onSubmitSuccess={() => setCompose(false)}
                      />
                    </div>
                  )}
                </>
              )}
              <p className="share-note">
                Disabling the link blocks new access to this page and its result
                preview. Your private result stays available. Screenshots,
                copied posts and third-party cached previews cannot be recalled.
                A later review revision is never substituted into this link.
              </p>
            </div>
          </>
        ))}
    </section>
  );
}
